"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import Link from "next/link";
import { Show, SignInButton } from "@clerk/nextjs";
import Pagination, { paginate } from "@/components/Pagination";
import { useI18n } from "@/lib/i18n";

const VACANCIES_PER_PAGE = 10;

/* ─── CRM Landing Page (unauthenticated) ─── */
function CrmLanding() {
  const { t } = useI18n();
  const features = [
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      title: t.crm.landing.feature1Title,
      desc: t.crm.landing.feature1Desc,
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      ),
      title: t.crm.landing.feature2Title,
      desc: t.crm.landing.feature2Desc,
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      title: t.crm.landing.feature3Title,
      desc: t.crm.landing.feature3Desc,
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      title: t.crm.landing.feature4Title,
      desc: t.crm.landing.feature4Desc,
    },
  ];

  // ── 3D parallax: smooth lerp on the scene, pointer-only, skipped on touch / reduced-motion ──
  const stageRef = useRef<HTMLDivElement | null>(null);
  const sceneRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const scene = sceneRef.current;
    const stage = stageRef.current;
    if (!scene || !stage) return;

    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const coarse = window.matchMedia?.("(pointer: coarse)").matches;
    if (reduce || coarse) return;

    const baseRX = 7;
    const baseRY = -15;
    let curRX = baseRX;
    let curRY = baseRY;
    let tgtRX = baseRX;
    let tgtRY = baseRY;
    let raf: number | null = null;

    function loop() {
      curRX += (tgtRX - curRX) * 0.09;
      curRY += (tgtRY - curRY) * 0.09;
      scene!.style.transform = `rotateX(${curRX.toFixed(2)}deg) rotateY(${curRY.toFixed(2)}deg)`;
      if (Math.abs(tgtRX - curRX) > 0.05 || Math.abs(tgtRY - curRY) > 0.05) {
        raf = requestAnimationFrame(loop);
      } else {
        raf = null;
      }
    }

    function onMove(ev: PointerEvent) {
      const r = stage!.getBoundingClientRect();
      const dx = (ev.clientX - (r.left + r.width / 2)) / r.width;
      const dy = (ev.clientY - (r.top + r.height / 2)) / r.height;
      tgtRX = Math.max(-12, Math.min(18, baseRX - dy * 9));
      tgtRY = Math.max(-30, Math.min(8, baseRY + dx * 16));
      if (raf == null) loop();
    }

    window.addEventListener("pointermove", onMove, { passive: true });
    return () => {
      window.removeEventListener("pointermove", onMove);
      if (raf != null) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div className="relative">
      {/* Hero */}
      <section id="top" className="relative overflow-hidden">
        <div
          className="max-w-6xl mx-auto px-6 py-16 sm:py-20 grid items-center gap-12 lg:gap-12"
          style={{ gridTemplateColumns: "repeat(auto-fit,minmax(380px,1fr))" }}
        >
          {/* LEFT: copy */}
          <div>
            <div className="inline-flex items-center gap-[9px] font-mono">
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: "#2F6BF6",
                  animation: "hf-pulse 2s infinite",
                }}
                className="hf-anim"
              />
              <span className="text-xs uppercase text-hf-muted" style={{ letterSpacing: ".07em" }}>
                {t.crm.landing.badge}
              </span>
            </div>

            <h1
              className="font-semibold text-hf-ink"
              style={{
                fontSize: "clamp(40px,5.4vw,66px)",
                lineHeight: 1.02,
                letterSpacing: "-.038em",
                maxWidth: "14ch",
                margin: "20px 0 0",
              }}
            >
              {t.crm.landing.title}
            </h1>

            <p
              className="text-hf-muted"
              style={{ maxWidth: 520, margin: "22px 0 0", fontSize: "18.5px", lineHeight: 1.55 }}
            >
              {t.crm.landing.subtitle}
            </p>

            <div className="flex flex-wrap gap-3" style={{ marginTop: 30 }}>
              <SignInButton mode="modal">
                <button
                  className="inline-flex items-center gap-2 text-white font-semibold transition-colors"
                  style={{
                    background: "#2F6BF6",
                    fontSize: 16,
                    padding: "14px 24px",
                    borderRadius: 13,
                    boxShadow: "0 10px 26px -10px rgba(47,107,246,.6)",
                  }}
                >
                  {t.crm.landing.openCrm}
                  <span style={{ fontSize: 18, lineHeight: 1 }}>&rarr;</span>
                </button>
              </SignInButton>

              <a
                href="#how"
                className="inline-flex items-center gap-2 text-hf-ink font-semibold transition-colors"
                style={{
                  border: "1px solid #DCDAD2",
                  fontSize: 16,
                  padding: "14px 22px",
                  borderRadius: 13,
                  textDecoration: "none",
                }}
              >
                {t.crm.landing.seeHow}
              </a>
            </div>

            <div className="flex flex-wrap" style={{ gap: 20, marginTop: 24 }}>
              {[
                t.crm.landing.trustFreeToStart,
                t.crm.landing.trustNoCard,
                t.crm.landing.trustSetup,
              ].map((label) => (
                <span
                  key={label}
                  className="font-mono inline-flex items-center text-hf-faint"
                  style={{ gap: 7, fontSize: "12.5px" }}
                >
                  <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#2F6BF6" }} />
                  {label}
                </span>
              ))}
            </div>
          </div>

          {/* RIGHT: 3D product scene */}
          <div className="hidden md:block">
            <div ref={stageRef} style={{ perspective: "1600px", perspectiveOrigin: "62% 38%", padding: "24px 6px" }}>
              <div
                ref={sceneRef}
                style={{
                  transformStyle: "preserve-3d",
                  transform: "rotateX(7deg) rotateY(-15deg)",
                  willChange: "transform",
                  position: "relative",
                }}
              >
                <div
                  className="hf-anim"
                  style={{
                    transformStyle: "preserve-3d",
                    animation: "hf-float 6.5s ease-in-out infinite",
                    position: "relative",
                  }}
                >
                  {/* Dashboard panel */}
                  <div
                    style={{
                      background: "#fff",
                      border: "1px solid #ECEAE3",
                      borderRadius: 18,
                      boxShadow:
                        "0 50px 90px -45px rgba(20,19,15,.5),0 8px 24px -12px rgba(20,19,15,.18)",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "14px 18px",
                        borderBottom: "1px solid #F1EFE9",
                        background: "#FCFBF8",
                      }}
                    >
                      <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#E6E3DB" }} />
                      <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#E6E3DB" }} />
                      <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#E6E3DB" }} />
                      <span style={{ marginLeft: 8, fontWeight: 600, fontSize: 14 }}>Vacancies</span>
                      <span
                        style={{
                          marginLeft: "auto",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          background: "#2F6BF6",
                          color: "#fff",
                          fontSize: "12.5px",
                          fontWeight: 600,
                          padding: "6px 11px",
                          borderRadius: 9,
                        }}
                      >
                        + New
                      </span>
                    </div>

                    <div style={{ padding: "6px 0" }}>
                      {[
                        { title: "Senior Backend Engineer", company: "Acme", count: 12, dot: "#1F9D57" },
                        { title: "Product Designer", company: "Nimbus", count: 7, dot: "#1F9D57" },
                        { title: "DevOps Engineer", company: "Acme", count: 4, dot: "#D6A53A" },
                        { title: "Frontend Engineer", company: "Loop", count: 9, dot: "#1F9D57", last: true },
                      ].map((row) => (
                        <div
                          key={row.title}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 14,
                            padding: "13px 18px",
                            borderBottom: row.last ? "none" : "1px solid #F4F2EC",
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: "14.5px" }}>{row.title}</div>
                            <div style={{ color: "#8A887F", fontSize: "12.5px" }}>{row.company}</div>
                          </div>
                          <span
                            className="font-mono"
                            style={{
                              fontSize: 12,
                              color: "#2F6BF6",
                              background: "#ECF1FE",
                              padding: "4px 9px",
                              borderRadius: 7,
                            }}
                          >
                            {row.count}
                          </span>
                          <span style={{ width: 8, height: 8, borderRadius: "50%", background: row.dot }} />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Floating candidate report card */}
                  <div
                    className="hf-anim"
                    style={{
                      position: "absolute",
                      top: -34,
                      right: -46,
                      width: 248,
                      transform: "translateZ(85px)",
                      animation: "hf-card 6.5s ease-in-out infinite",
                      background: "#fff",
                      border: "1px solid #ECEAE3",
                      borderRadius: 16,
                      boxShadow: "0 40px 70px -30px rgba(20,19,15,.55)",
                      padding: 16,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                      <div
                        style={{
                          width: 42,
                          height: 42,
                          borderRadius: "50%",
                          background: "conic-gradient(#2F6BF6 0 87%,#ECEAE3 87% 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flex: "none",
                        }}
                      >
                        <span
                          className="font-mono"
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            background: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 13,
                            fontWeight: 500,
                            color: "#15140F",
                          }}
                        >
                          87
                        </span>
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>Maria K.</div>
                        <div style={{ color: "#8A887F", fontSize: 12 }}>Senior Backend · Acme</div>
                      </div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 14 }}>
                      {[
                        { label: "System design", tag: "Strong", w: "88%", color: "#2F6BF6" },
                        { label: "Concurrency", tag: "Good", w: "72%", color: "#2F6BF6" },
                        { label: "Ownership", tag: "Gap", w: "41%", color: "#D6A53A" },
                      ].map((b) => (
                        <div key={b.label}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              fontSize: 11,
                              color: "#8A887F",
                              marginBottom: 3,
                            }}
                          >
                            <span>{b.label}</span>
                            <span>{b.tag}</span>
                          </div>
                          <div style={{ height: 5, borderRadius: 3, background: "#F1EFE9" }}>
                            <div style={{ width: b.w, height: "100%", borderRadius: 3, background: b.color }} />
                          </div>
                        </div>
                      ))}
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14 }}>
                      <span
                        style={{
                          flex: 1,
                          textAlign: "center",
                          background: "#2F6BF6",
                          color: "#fff",
                          fontSize: "12.5px",
                          fontWeight: 600,
                          padding: 8,
                          borderRadius: 9,
                        }}
                      >
                        Advance &#10003;
                      </span>
                      <div style={{ display: "flex", alignItems: "center", gap: "2.5px", height: 16 }}>
                        {[
                          { dur: ".9s", delay: "0s" },
                          { dur: ".8s", delay: "-.3s" },
                          { dur: "1.05s", delay: "-.15s" },
                        ].map((bar, i) => (
                          <span
                            key={i}
                            className="hf-anim"
                            style={{
                              display: "block",
                              width: "2.5px",
                              height: 16,
                              borderRadius: 2,
                              background: "#2F6BF6",
                              transformOrigin: "center",
                              animation: `hf-eq ${bar.dur} ease-in-out ${bar.delay} infinite`,
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Floating status chip */}
                  <div
                    className="hf-anim"
                    style={{
                      position: "absolute",
                      bottom: -26,
                      left: -26,
                      transform: "translateZ(120px)",
                      animation: "hf-chip 5.5s ease-in-out infinite",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      background: "#15140F",
                      color: "#fff",
                      fontSize: "12.5px",
                      fontWeight: 500,
                      padding: "9px 14px",
                      borderRadius: 11,
                      boxShadow: "0 24px 44px -20px rgba(20,19,15,.6)",
                    }}
                  >
                    <span
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        background: "#1F9D57",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 10,
                      }}
                    >
                      &#10003;
                    </span>
                    Interview completed
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <div id="how" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 scroll-mt-20">
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-hf-ink mb-3">
            {t.crm.landing.sectionTitle}
          </h2>
          <p className="text-hf-muted max-w-xl mx-auto">
            {t.crm.landing.sectionSubtitle}
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          {features.map((f) => (
            <div
              key={f.title}
              className="p-6 rounded-2xl bg-hf-card border border-hf-line hover:border-hf-primary/30 transition-colors"
            >
              <div className="w-12 h-12 rounded-lg bg-hf-primary-soft flex items-center justify-center text-hf-primary mb-4">
                {f.icon}
              </div>
              <h3 className="text-lg font-semibold text-hf-ink mb-2">{f.title}</h3>
              <p className="text-hf-muted text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <SignInButton mode="modal">
            <button className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-hf-primary hover:bg-hf-primary-strong text-white font-medium transition-all shadow-[0_10px_30px_-15px_rgba(20,19,15,0.25)]">
              {t.crm.landing.signInCta}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          </SignInButton>
        </div>
      </div>
    </div>
  );
}


/* ─── CRM Dashboard (authenticated) ─── */
function CrmDashboard() {
  const { t } = useI18n();
  const [vacancies, setVacancies] = useState<VacancyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const fetchVacancies = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/crm/vacancies");
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to load vacancies");
      }
      const data = await res.json();
      setVacancies(data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load vacancies");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVacancies();
  }, [fetchVacancies]);

  function buildPublicUrl(v: VacancyRow) {
    const origin = typeof window !== "undefined" ? window.location.origin : "https://b2b.garant.cloud";
    return `${origin}/${v.companies.slug}/${v.slug}`;
  }

  async function handleCopy(v: VacancyRow) {
    try {
      await navigator.clipboard.writeText(buildPublicUrl(v));
      setCopiedId(v.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // fallback: ignore
    }
  }

  async function handleDelete(id: string) {
    if (!confirm(t.crm.dashboard.deleteConfirm)) return;
    setDeletingId(id);
    try {
      const res = await fetch(`/api/crm/vacancies/${id}`, { method: "DELETE" });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to delete");
      }
      await fetchVacancies();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : "Failed to delete vacancy");
    } finally {
      setDeletingId(null);
    }
  }

  function formatDate(iso: string) {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-2 border-hf-primary border-t-transparent rounded-full animate-spin" />
          <span className="ml-3 text-hf-muted">{t.crm.dashboard.loading}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-600 mb-3">{error}</p>
          <button
            onClick={fetchVacancies}
            className="px-4 py-2 rounded-lg bg-hf-primary hover:bg-hf-primary-strong text-white text-sm font-medium transition-colors"
          >
            {t.crm.dashboard.retry}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-hf-ink">{t.crm.dashboard.title}</h1>
          <p className="text-hf-muted text-sm mt-1">
            {t.crm.dashboard.subtitle}
          </p>
        </div>
        <Link
          href="/crm/vacancies/new"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-hf-primary hover:bg-hf-primary-strong text-white text-sm font-medium transition-colors shadow-[0_10px_30px_-15px_rgba(20,19,15,0.25)]"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t.crm.dashboard.newVacancy}
        </Link>
      </div>

      {/* Empty state */}
      {vacancies.length === 0 ? (
        <div className="bg-hf-card border border-hf-line rounded-xl p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-hf-primary-soft flex items-center justify-center">
            <svg className="w-8 h-8 text-hf-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-hf-ink mb-2">{t.crm.dashboard.emptyTitle}</h2>
          <p className="text-hf-muted mb-6">{t.crm.dashboard.emptyDesc}</p>
          <Link
            href="/crm/vacancies/new"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-hf-primary hover:bg-hf-primary-strong text-white text-sm font-medium transition-colors shadow-[0_10px_30px_-15px_rgba(20,19,15,0.25)]"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t.crm.dashboard.emptyCta}
          </Link>
        </div>
      ) : (
        /* Vacancy table */
        <div className="bg-hf-card border border-hf-line rounded-xl overflow-hidden">
          {/* Table header */}
          <div className="hidden sm:grid sm:grid-cols-[1fr_140px_100px_1fr_80px] gap-4 px-6 py-3 border-b border-hf-line text-xs font-medium text-hf-muted uppercase tracking-wider">
            <span>{t.crm.dashboard.colVacancy}</span>
            <span>{t.crm.dashboard.colCreated}</span>
            <span>{t.crm.dashboard.colCandidates}</span>
            <span>{t.crm.dashboard.colPublicUrl}</span>
            <span className="text-right">{t.crm.dashboard.colActions}</span>
          </div>

          {/* Rows */}
          {paginate(vacancies, page, VACANCIES_PER_PAGE).map((v) => (
            <div
              key={v.id}
              className="group sm:grid sm:grid-cols-[1fr_140px_100px_1fr_80px] gap-4 px-6 py-4 border-b border-hf-line last:border-b-0 hover:bg-hf-surface transition-colors items-center"
            >
              {/* Title + company */}
              <div className="min-w-0">
                <Link
                  href={`/crm/vacancies/${v.id}`}
                  className="text-hf-ink font-medium hover:text-hf-primary transition-colors block truncate"
                >
                  {v.title}
                </Link>
                <span className="text-hf-faint text-sm truncate block">
                  {v.companies?.name || "—"}
                </span>
              </div>

              {/* Date */}
              <div className="text-hf-muted text-sm mt-1 sm:mt-0">
                {formatDate(v.created_at)}
              </div>

              {/* Candidate count */}
              <div className="mt-1 sm:mt-0">
                <span className="inline-flex items-center gap-1 text-sm text-hf-muted">
                  <svg className="w-4 h-4 text-hf-faint" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {v.candidate_count}
                </span>
              </div>

              {/* Public URL + copy */}
              <div className="flex items-center gap-2 min-w-0 mt-1 sm:mt-0">
                <span className="text-hf-faint text-sm truncate block">
                  {buildPublicUrl(v).replace("https://", "")}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopy(v);
                  }}
                  className="flex-shrink-0 relative p-1.5 rounded-md hover:bg-hf-primary-soft text-hf-muted hover:text-hf-primary transition-colors"
                  title={t.crm.dashboard.copyUrl}
                >
                  {copiedId === v.id ? (
                    <span className="text-hf-success text-xs font-medium whitespace-nowrap">{t.crm.dashboard.copied}</span>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  )}
                </button>
              </div>

              {/* Delete */}
              <div className="flex justify-end mt-2 sm:mt-0">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(v.id);
                  }}
                  disabled={deletingId === v.id}
                  className="p-1.5 rounded-md text-hf-faint hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                  title={t.crm.dashboard.deleteVacancy}
                >
                  {deletingId === v.id ? (
                    <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {vacancies.length > VACANCIES_PER_PAGE && (
        <Pagination
          className="mt-4"
          total={vacancies.length}
          perPage={VACANCIES_PER_PAGE}
          page={page}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}


/* ─── Types ─── */
interface VacancyCompany {
  id: string;
  name: string;
  slug: string;
}

interface VacancyRow {
  id: string;
  title: string;
  slug: string;
  created_at: string;
  candidate_count: number;
  companies: VacancyCompany;
}

/* ─── Page: conditionally render landing or dashboard ─── */
export default function CrmPage() {
  return (
    <>
      <Show when="signed-out">
        <CrmLanding />
      </Show>
      <Show when="signed-in">
        <CrmDashboard />
      </Show>
    </>
  );
}
